import {
  BorderColor,
  Check,
  CheckBoxOutlined,
  Close,
  InfoOutlined,
} from "@mui/icons-material";
import {
  Box,
  Typography,
  IconButton as MuiIconButton,
  Tooltip,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CheckboxOutlined,
  DrawerComponent,
  IconButton,
  NumberTextField,
  EConfirmModalType,
  NoteComponent,
} from "src/shared/components";
import { PartnerLabel } from "src/shared/components/PartnerLabel";
import { OriginalContractAvailabilityValues } from "src/shared/constants/agreements.constans";
import { currenciesSymbols } from "src/shared/constants/currency.constants";
import { useConfirmModal } from "src/shared/hooks";
import { useTableCheckboxesState } from "src/shared/hooks/useGetTableCheckboxesState";
import { useAuth } from "src/shared/lib/rback/AuthContext";
import { Can } from "src/shared/lib/rback/Can";
import { EIconNames } from "src/shared/types/iconNames";
import { UpdateRewardForm } from "../../ui/UpdateRewardForm";
import {
  usePostBonusStatesMutation,
  useUpdateBonusMutation,
  useUpdateBonusNoteMutation,
  useUpdateBonusWithActMutation,
} from "../api/api";
import { getPostBonusStateErrors } from "../helpers/errors.helpers";
import {
  EBonusState,
  IBonusStateItem,
  IPostBonusDTO,
  ISummaryReportItem,
  summaryReportStatuses,
} from "../types";

export function useGetTableColumns(data: ISummaryReportItem[] = []) {
  const { hasPermission } = useAuth();
  const [searchParams] = useSearchParams();
  const reportMonth = searchParams.get("report_month");
  const [noteModalData, setNoteModalData] = useState<{
    id: number;
    note: string;
  } | null>(null);
  const [drawerData, setDrawerData] = useState<ISummaryReportItem | null>(null);
  const disabledStates = useMemo(
    () => [
      EBonusState.ACT_IS_SENT,
      EBonusState.ACT_IS_PAID,
      EBonusState.ACT_IS_RECEIVED,
      EBonusState.ACT_CARRIED_OUT,
    ],
    [],
  );

  const disabledIds = useMemo(() => {
    return new Set(
      data
        .filter((row) => disabledStates.includes(row.state))
        .map((row) => String(row.id)),
    );
  }, [data, disabledStates]);

  const [deleteNoteModalData, setDeleteNoteModalData] = useState<{
    id: number;
    note: string;
  } | null>(null);

  const [updateBonusRequest, { isLoading: isLoadingUpdateBonus }] =
    useUpdateBonusMutation({ fixedCacheKey: "updateBonus" });
  const [
    updateBonusWithActRequest,
    { isLoading: isLoadingUpdateBonusWithAct },
  ] = useUpdateBonusWithActMutation();
  const [updateBonusNoteRequest, { isLoading: isLoadingUpdateNote }] =
    useUpdateBonusNoteMutation();

  const [postBonusStateRequest, { isLoading: isLoadingPostBonusState }] =
    usePostBonusStatesMutation();

  async function postBonusState(data: IBonusStateItem[]) {
    try {
      const res = await postBonusStateRequest(data).unwrap();
      const errors = getPostBonusStateErrors(res.data);
      if (errors.length) {
        enqueueSnackbar(errors.join("\n"), {
          variant: "error",
          handleClose: closeSnackbar,
        });
      }
    } catch {
      enqueueSnackbar("Произошла ошибка", {
        variant: "error",
        handleClose: closeSnackbar,
      });
    }
  }

  async function updateBonus(data: IPostBonusDTO) {
    try {
      await updateBonusRequest(data).unwrap();
    } catch (e) {
      enqueueSnackbar("Произошла ошибка", {
        variant: "error",
        handleClose: closeSnackbar,
      });
    }
  }
  async function updateBonusWithAct(data: IPostBonusDTO) {
    try {
      await updateBonusWithActRequest(data).unwrap();
      setDrawerData(null);
    } catch (e: unknown) {
      let message = "Произошла ошибка";

      if (typeof e === "object" && e && "data" in e) {
        const err = e as { data?: { message?: string } };
        message = err.data?.message ?? message;
      }

      enqueueSnackbar(message, {
        variant: "error",
        handleClose: closeSnackbar,
      });
    }
  }

  const {
    modal: deleteNoteConfirmModal,
    showModal,
    closeModal,
  } = useConfirmModal({
    onResolve: () => {
      if (deleteNoteModalData) {
        updateBonusNote(deleteNoteModalData);
      }
    },
    isLoading: isLoadingUpdateNote,
    confirmModalType: EConfirmModalType.WARNING,

    children: <Typography>Удалить заметку?</Typography>,
  });
  async function updateBonusNote(data: { note: string; id: number }) {
    try {
      await updateBonusNoteRequest(data).unwrap();
      closeModal();
      setDeleteNoteModalData(null);
      setNoteModalData(null);
    } catch (e) {
      enqueueSnackbar("Произошла ошибка", {
        variant: "error",
        handleClose: closeSnackbar,
      });
    }
  }

  const {
    tableCheckedState,
    checkAll,
    checkSingle,
    getChecked,
    resetTableCheckedState,
    isIndeterminate,
  } = useTableCheckboxesState(disabledIds);

  const {
    tableCheckedState: tableEmailsCheckedState,
    checkAll: checkAllEmails,
    checkSingle: checkSingleEmail,
    getChecked: getCheckedEmails,
    resetTableCheckedState: resetTableCheckedEmailsState,
    isIndeterminate: isIndeterminateEmails,
  } = useTableCheckboxesState();

  const noteModal = (
    <NoteComponent
      key={noteModalData?.id}
      open={Boolean(noteModalData)}
      onClose={() => setNoteModalData(null)}
      value={noteModalData?.note || ""}
      handleSave={(v) => {
        if (noteModalData) {
          const updatedNoteData = { ...noteModalData, note: v };
          updateBonusNote(updatedNoteData);
        }
      }}
      isLoading={isLoadingUpdateNote}
    />
  );
  const editBonusDrawer = (
    <DrawerComponent
      key={drawerData?.id}
      open={!!drawerData}
      anchor="right"
      onClose={() => setDrawerData(null)}
      title="Редактирование отчета"
      header={
        <Box
          sx={{
            flexDirection: "column",
            display: "flex",
            width: "100%",
          }}
        >
          <Typography variant="h6">Редактирование отчета</Typography>
          <Typography
            sx={{
              fontSize: "0.875rem",
              color: "grey.600",
            }}
          >
            {drawerData?.partner.name}
          </Typography>
        </Box>
      }
    >
      {drawerData ? (
        <UpdateRewardForm
          isLoading={isLoadingUpdateBonusWithAct}
          handleSave={({ one_time_reward, active_accounts_quantity }) => {
            updateBonusWithAct({
              id: drawerData.id,
              one_time_reward,
              active_accounts_quantity,
            });
          }}
          reward={drawerData}
        />
      ) : null}
    </DrawerComponent>
  );

  const handleActDownload = async (url: string | null, partnerName: string) => {
    if (!url) {
      enqueueSnackbar("Акт не сформирован", {
        variant: "error",
        handleClose: closeSnackbar,
      });

      return;
    }

    try {
      const response = await fetch(url);

      if (!response.ok) {
        enqueueSnackbar("Ошибка при скачивании акта", {
          variant: "error",
          handleClose: closeSnackbar,
        });

        return;
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      const filename =
        reportMonth && partnerName
          ? `${partnerName} ${reportMonth}.pdf`
          : `act.pdf`;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      enqueueSnackbar("Ошибка при скачивании файла", {
        variant: "error",
        handleClose: closeSnackbar,
      });
    }
  };

  const columns: GridColDef<ISummaryReportItem>[] = [
    {
      headerName: "",
      type: "custom",
      field: "__CHECKED__2",
      width: 60,
      align: "center",
      sortable: false,
      headerClassName: "no-border-right",
      cellClassName: "no-border-right  vertical-align-center",
      renderCell: ({ row }) => {
        if (row.state === EBonusState.ACT_FORMED) {
          return (
            <Can perform="partners.summary-report.edit-act">
              <MuiIconButton onClick={() => setDrawerData(row)} sx={{ p: 1 }}>
                <Typography
                  sx={{
                    color: "grey.700",
                  }}
                >
                  <BorderColor />
                </Typography>
              </MuiIconButton>
            </Can>
          );
        }

        if (row.state === EBonusState.CHECKED) {
          return (
            <CheckboxOutlined
              sx={{
                ["& svg"]: {
                  color: "primary.main",
                  opacity: isLoadingPostBonusState ? 0.5 : undefined,
                },
              }}
              checked
              disabled={isLoadingPostBonusState}
              onChange={(e) => {
                postBonusState([
                  {
                    id: row.id,
                    is_checked: e.target.checked,
                  },
                ]);
              }}
            />
          );
        }

        if (row.state === EBonusState.RESERVED_FOR_1C) {
          return (
            <Tooltip title="Подготовка акта в 1С, перезагрузите страницу через минуту">
              <InfoOutlined sx={{ color: "grey.700" }} />
            </Tooltip>
          );
        }

        return (
          <Can perform="partners.summary-report.edit">
            <CheckboxOutlined
              color="default"
              checked={getChecked(String(row.id))}
              disabled={disabledStates.includes(row.state)}
              onChange={(e) => {
                checkSingle(String(row.id), e.target.checked);
              }}
            />
          </Can>
        );
      },
      renderHeader: () => {
        return (
          <Can perform="partners.summary-report.edit">
            <CheckboxOutlined
              color="default"
              indeterminate={isIndeterminate}
              checked={tableCheckedState.all}
              onChange={(e) => checkAll(e.target.checked)}
            />
          </Can>
        );
      },
    },
    {
      headerName: "Партнер",
      type: "string",
      field: "partner",
      maxWidth: 200,
      width: 150,
      renderCell: ({ row }) => (
        <PartnerLabel
          name={row.partner.name}
          organizationForm={row.partner.organization_form?.value}
        />
      ),
    },
    {
      headerName: "КД Партнера",
      type: "string",
      field: "partner_contract_number",
      width: 120,
    },
    {
      headerName: "с НДС",
      type: "custom",
      field: "tax_system",
      align: "center",
      width: 100,
      cellClassName: "vertical-align-center",
      renderCell({ row }) {
        if (row.tax_system) {
          return <Check color="success" />;
        }

        return null;
      },
    },
    {
      headerName: "Кол-во активных аккаунтов",
      type: "custom",
      field: "active_accounts_quantity",
      width: 120,
      cellClassName: "vertical-align-center",
      align: "center",
      renderCell: ({ row, value }) => {
        if (
          row.state === EBonusState.ACT_FORMED ||
          row.state === EBonusState.RESERVED_FOR_1C ||
          row.state === EBonusState.CHECKED
        ) {
          return value;
        }

        return (
          <Box
            sx={{
              width: 50,
              display: "flex",
              alignContent: "center",
            }}
          >
            <NumberTextField
              disabled={
                isLoadingUpdateBonus ||
                !hasPermission("partners.summary-report.edit") ||
                disabledStates.includes(row.state)
              }
              max={9999999}
              defaultValue={value}
              size="small"
              sx={{ ["& input"]: { textAlign: "center" } }}
              onBlur={(e) => {
                const inputValue = e.target.value;
                if (value == inputValue) return;
                const { one_time_reward, id } = row;
                updateBonus({
                  id,
                  one_time_reward,
                  active_accounts_quantity: inputValue,
                });
              }}
            />
          </Box>
        );
      },
    },
    {
      headerName: "Общая сумма открутки",
      type: "string",
      field: "total_spend",
      width: 120,
      valueFormatter(v, row) {
        if (v === null || v === undefined) return "";

        return `${v} ${currenciesSymbols[row.complex_contract_currency]}`;
      },
    },
    {
      headerName: "Сумма Бонуса по открутке",
      type: "string",
      field: "bonus_sum_by_spend",
      width: 120,
      valueFormatter(v, row) {
        if (v === null || v === undefined) return "";

        return `${v} ${currenciesSymbols[row.complex_contract_currency]}`;
      },
    },
    {
      headerName: "Накопление предыдущих периодов",
      type: "string",
      field: "previous_period_accumulation",
      width: 120,
      valueFormatter(v, row) {
        if (v === null || v === undefined) return "";

        return `${v} ${currenciesSymbols[row.complex_contract_currency]}`;
      },
    },
    {
      headerName: "Единовременное вознаграждение",
      type: "custom",
      field: "one_time_reward",
      width: 120,
      cellClassName: "vertical-align-center",
      renderCell: ({ row, value }) => {
        if (
          row.state === EBonusState.ACT_FORMED ||
          row.state === EBonusState.RESERVED_FOR_1C ||
          row.state === EBonusState.CHECKED
        ) {
          return (
            <>{`${value} ${currenciesSymbols[row.complex_contract_currency]}`}</>
          );
        }

        return (
          <NumberTextField
            disabled={
              isLoadingUpdateBonus ||
              !hasPermission("partners.summary-report.edit") ||
              disabledStates.includes(row.state)
            }
            max={9999999}
            defaultValue={value}
            size="small"
            decimalScale={2}
            slotProps={{
              input: {
                endAdornment: (
                  <Typography color="grey.700" fontSize="0.875rem">
                    {row.complex_contract_currency === null ||
                    row.complex_contract_currency === undefined
                      ? ""
                      : currenciesSymbols[row.complex_contract_currency]}
                  </Typography>
                ),
              },
            }}
            onBlur={(e) => {
              const inputValue = e.target.value;
              if (value == inputValue) return;
              const { id, active_accounts_quantity } = row;
              updateBonus({
                id,
                one_time_reward: inputValue,
                active_accounts_quantity,
              });
            }}
          />
        );
      },
    },
    {
      headerName: "Бонус к выплате в валюте КД",
      type: "string",
      field: "bonus_to_pay",
      width: 120,
      valueFormatter(v, row) {
        if (v === null || v === undefined) return "";

        return `${v} ${currenciesSymbols[row.complex_contract_currency]}`;
      },
    },
    {
      headerName: "Примечание",
      type: "custom",
      field: "note",
      width: 140,
      renderCell: ({ row }) => {
        const { note, id } = row;

        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            {note ? (
              <Box
                sx={{
                  display: "flex",
                  gap: 0.5,
                  paddingLeft: 8.5,
                  ["&:hover button:nth-of-type(2)"]: { visibility: "visible" },
                }}
              >
                <Can perform="partners.summary-report.edit">
                  <IconButton
                    onClick={() =>
                      setNoteModalData({
                        id,
                        note,
                      })
                    }
                    sx={{ color: (theme) => theme.palette.primary.main }}
                    iconName={EIconNames.NOTE}
                    disabled={disabledStates.includes(row.state)}
                  />
                  <IconButton
                    sx={{ visibility: "hidden" }}
                    onClick={() => {
                      setDeleteNoteModalData({
                        id,
                        note: "",
                      });
                      showModal();
                    }}
                    iconName={EIconNames.CROSS_BUTTON}
                    disabled={disabledStates.includes(row.state)}
                  />
                </Can>
              </Box>
            ) : (
              <Can perform="partners.summary-report.edit">
                <IconButton
                  disabled={disabledStates.includes(row.state)}
                  iconName={EIconNames.PLUS_BUTTON}
                  onClick={() =>
                    setNoteModalData({
                      id,
                      note,
                    })
                  }
                />
              </Can>
            )}
          </Box>
        );
      },
    },
    {
      headerName: "",
      type: "custom",
      field: "__check__1",
      width: 60,
      align: "center",
      sortable: false,
      headerClassName: "no-border-right",
      cellClassName: "no-border-right",
      renderCell: ({ row }) => {
        return (
          <Tooltip
            title={
              row.state !== EBonusState.ACT_FORMED
                ? "Для партнера не сформирован акт"
                : ""
            }
          >
            <Can perform="partners.summary-report.edit">
              <span>
                <CheckboxOutlined
                  disabled={row.state !== EBonusState.ACT_CARRIED_OUT}
                  checkedIcon={<CheckBoxOutlined />}
                  color="default"
                  checked={
                    row.state === EBonusState.ACT_CARRIED_OUT &&
                    getCheckedEmails(String(row.id))
                  }
                  onChange={(e) =>
                    checkSingleEmail(String(row.id), e.target.checked)
                  }
                />
              </span>
            </Can>
          </Tooltip>
        );
      },
      renderHeader: () => {
        return (
          <CheckboxOutlined
            color="default"
            indeterminate={isIndeterminateEmails}
            checked={tableEmailsCheckedState.all}
            onChange={(e) => checkAllEmails(e.target.checked)}
          />
        );
      },
    },
    {
      headerName: "Email партнера",
      type: "string",
      field: "partner_email",
      width: 160,
    },
    {
      headerName: "Наличие справки об НДС",
      type: "custom",
      field: "has_tax_certificate",
      align: "center",
      cellClassName: "vertical-align-center",
      width: 100,
      renderCell({ row }) {
        if (row.has_tax_certificate === null) return null;

        return row.has_tax_certificate ? (
          <Check color="success" />
        ) : (
          <Close color="error" />
        );
      },
    },
    {
      headerName: "Статус",
      field: "state",
      align: "center",
      cellClassName: "vertical-align-center",
      width: 200,
      renderCell({ row }) {
        const errors = Array.isArray(row.errors) ? row.errors : [];
        const hasErrors = errors.length > 0;
        const statusText = summaryReportStatuses[row.state];

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            {hasErrors && (
              <Tooltip
                title={
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      maxWidth: 300,
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    {errors.map((err, i) => (
                      <Typography key={i} variant="caption">
                        {err.message}
                      </Typography>
                    ))}
                  </Box>
                }
              >
                <span>
                  <IconButton
                    iconName={EIconNames.WARNING}
                    size="small"
                    sx={{
                      color: "warning.main",
                      p: 0,
                      width: 30,
                      height: 30,
                    }}
                  />
                </span>
              </Tooltip>
            )}

            <Typography variant="body2">{statusText}</Typography>
          </Box>
        );
      },
    },
    {
      headerName: "Оригинал договора",
      type: "custom",
      field: "has_contract_original",
      align: "center",
      cellClassName: "vertical-align-center",
      width: 120,
      renderCell: ({ row }) =>
        OriginalContractAvailabilityValues[
          row.contract_original_availability.value
        ],
    },
    {
      headerName: "ДС",
      type: "custom",
      field: "extra_agreement_has_original",
      align: "center",
      cellClassName: "vertical-align-center",
      width: 100,
      renderCell({ row }) {
        if (row.extra_agreement_has_original === null) return null;

        return row.extra_agreement_has_original ? (
          <Check color="success" />
        ) : (
          <Close color="error" />
        );
      },
    },
    {
      headerName: "",
      type: "custom",
      sortable: false,
      field: "__download__",
      align: "center",
      width: 100,
      renderCell({ row }) {
        return (
          <Can perform="partners.summary-report.edit">
            <IconButton
              onClick={() => handleActDownload(row.act_url, row.partner?.name)}
              size="large"
              sx={{ color: (theme) => theme.palette.grey[700] }}
              iconName={EIconNames.DOWNLOAD}
            />
          </Can>
        );
      },
    },
  ];

  return {
    columns,
    noteModal,
    deleteNoteConfirmModal,
    editBonusDrawer,
    tableCheckedState,
    tableEmailsCheckedState,
    resetTableCheckedEmailsState,
    resetTableCheckedState,
  };
}
